import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { alertsAPI, cargosAPI, laudosAPI, pagamentosAPI, getEmpresaPublicInfo } from '../lib/api';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from './ui/sheet';
import VencimentoBanner from './VencimentoBanner';
import ChatWidget from './chat/ChatWidget';
import AlertasWA from '../pages/whatsapp/AlertasWA';
import { useModulosAtivos } from '../hooks/useModulosAtivos';
import {
  Leaf,
  LayoutDashboard,
  Users,
  FolderOpen,
  Settings,
  FileText,
  Handshake,
  CheckSquare,
  Archive,
  PauseCircle,
  Sun,
  Moon,
  Menu,
  LogOut,
  ChevronRight,
  ChevronLeft,
  Bell,
  MessageCircle,
  AlertTriangle,
  Trash2,
  Palette,
  HelpCircle,
  BookOpen,
  Building2,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronDown,
  ChevronUp,
  Shield,
  Tag,
  ClipboardList,
  FileSignature,
  DollarSign,
  Wallet,
  Receipt,
  PiggyBank,
  CheckCircle,
  BarChart3,
  QrCode,
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const allMenuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
  { icon: FolderOpen, label: 'Projetos', path: '/projetos' },
  { icon: Users, label: 'Clientes', path: '/clientes' },
  { icon: FileText, label: 'Propostas', path: '/propostas' },
  { icon: ClipboardList, label: 'Atividades', path: '/atividades' },
  { icon: FolderOpen, label: 'Iniciar Projeto', path: '/projetos/novo' },
  // Projetos Suspensos: visível para quem tem acesso à página Projetos
  { icon: PauseCircle, label: 'Projetos Suspensos', path: '/projetos/suspensos', requires: '/projetos' },
  { icon: Archive, label: 'Projetos Finalizados', path: '/projetos/arquivados' },
  { icon: FileText, label: 'Relatórios', path: '/relatorios' },
  { icon: HelpCircle, label: 'Suporte', path: '/suporte' },
];

const allAdminMenuItems = [
  { icon: Handshake, label: 'Parceiros', path: '/admin/parceiros' },
  { icon: Users, label: 'Usuários', path: '/admin/usuarios' },
  { icon: CheckSquare, label: 'Etapas', path: '/admin/etapas' },
  { icon: FolderOpen, label: 'Categorias', path: '/admin/categorias' },
  { icon: Tag, label: 'Marcas', path: '/admin/marcas' },
  { icon: Palette, label: 'Status Projeto', path: '/admin/status-projeto' },
  { icon: FolderOpen, label: 'Tipos de Projeto', path: '/admin/tipos-projeto' },
  { icon: Settings, label: 'Cargos', path: '/admin/cargos' },
  { icon: Building2, label: 'Empresa', path: '/admin/empresa' },
];

const allMasterMenuItems = [
  { icon: Trash2, label: 'Eliminar Dados', path: '/admin/eliminar-dados' },
];

const allFinanceiroMenuItems = [
  { icon: LayoutDashboard, label: 'Dashboard Financeiro', path: '/financeiro' },
  { icon: Receipt, label: 'Serviços', path: '/financeiro/servicos' },
  { icon: CheckCircle, label: 'Serviços Finalizados', path: '/financeiro/finalizados' },
  { icon: PiggyBank, label: 'Despesas', path: '/financeiro/despesas' },
  { icon: BarChart3, label: 'Relatórios', path: '/financeiro/relatorio' },
  { icon: Settings, label: 'Configurações Financeiro', path: '/financeiro/configuracoes' },
];

const allWhatsappMenuItems = [
  { icon: MessageCircle, label: 'Atendimento', path: '/whatsapp' },
  { icon: Users, label: 'Central de Contatos', path: '/whatsapp/contatos' },
  { icon: FileText, label: 'Templates', path: '/whatsapp/templates' },
  { icon: Settings, label: 'Configurações', path: '/whatsapp/chatbot' },
  { icon: BarChart3, label: 'Painel e relatórios', path: '/whatsapp/analytics' },
  { icon: QrCode, label: 'Conexão', path: '/whatsapp/conexao' },
];

const TODOS_PATHS_MENU = [
  ...allMenuItems, ...allAdminMenuItems, ...allMasterMenuItems, ...allFinanceiroMenuItems, ...allWhatsappMenuItems,
].map(i => i.path);

const SidebarContent = ({ onItemClick, userPermissions }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { financeiroAtivo, whatsappAtivo } = useModulosAtivos();
  const [logoUrl, setLogoUrl] = useState(null);
  const [nomeEmpresa, setNomeEmpresa] = useState('');
  const [ocultarPropostas, setOcultarPropostas] = useState(false);
  const [adminOpen, setAdminOpen] = useState(() => {
    // Auto-abrir se já estiver em rota admin
    return location.pathname.startsWith('/admin');
  });
  const [finOpen, setFinOpen] = useState(() => location.pathname.startsWith('/financeiro'));
  const [waOpen, setWaOpen] = useState(() => location.pathname.startsWith('/whatsapp'));

  useEffect(() => {
    const img = new Image();
    img.src = `${API_URL}/api/empresa/logo?t=${Date.now()}`;
    img.onload = () => setLogoUrl(img.src);
    img.onerror = () => setLogoUrl(null);
    // Nome da empresa e páginas ocultas
    const aplicar = (data) => {
      if (data.nome_empresa) setNomeEmpresa(data.nome_empresa);
      setOcultarPropostas(!!data.ocultar_propostas);
    };
    getEmpresaPublicInfo().then(aplicar);
    const onInfo = (e) => aplicar(e.detail || {});
    window.addEventListener('empresa-info', onInfo);
    return () => window.removeEventListener('empresa-info', onInfo);
  }, []);

  // Destaca só o item mais específico (ex.: em /projetos/suspensos não destaca "Projetos")
  const isActive = (path) => {
    const atual = location.pathname;
    if (path === '/') return atual === '/';
    const casa = (p) => atual === p || atual.startsWith(p + '/');
    if (!casa(path)) return false;
    return !TODOS_PATHS_MENU.some(p => p !== path && p.startsWith(path + '/') && casa(p));
  };

  // Filtrar menus baseado nas permissões do usuário
  const hasPermission = (path) => {
    if (!userPermissions || userPermissions.length === 0) return false;
    return userPermissions.includes(path);
  };

  // Rotas que aparecem para todos os usuários logados (sem precisar de permissão)
  const publicRoutes = ['/suporte', '/base-conhecimento'];
  
  // Rotas especiais que aparecem para master mesmo sem estar nas permissões do backend
  const specialMasterRoutes = ['/admin/tenants'];
  const isMaster = user?.role === 'master' || user?.cargo === 'master';

  const menuItems = allMenuItems.filter(item =>
    (hasPermission(item.requires || item.path) || publicRoutes.includes(item.path)) &&
    !(ocultarPropostas && item.path === '/propostas')
  );
  const adminMenuItems = allAdminMenuItems.filter(item => hasPermission(item.path));
  const masterMenuItems = allMasterMenuItems.filter(item => 
    hasPermission(item.path) || (isMaster && specialMasterRoutes.includes(item.path))
  );
  const finMenuItems = financeiroAtivo
    ? allFinanceiroMenuItems.filter(item => hasPermission(item.path))
    : [];
  const waMenuItems = whatsappAtivo
    ? allWhatsappMenuItems.filter(item => hasPermission(item.path))
    : [];

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-border">
        <Link to="/" className="flex items-center gap-3" onClick={onItemClick}>
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="w-10 h-10 object-contain" />
          ) : (
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <Leaf className="w-6 h-6 text-primary-foreground" />
            </div>
          )}
          <span className="text-xl font-bold tracking-tight">{nomeEmpresa || 'AgroLink'}</span>
        </Link>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <nav className="px-3 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onItemClick}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group',
                  isActive(item.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                )}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
                {isActive(item.path) && (
                  <ChevronRight className="w-4 h-4 ml-auto" />
                )}
              </Link>
            );
          })}

          {finMenuItems.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setFinOpen((v) => !v)}
                data-testid="nav-financeiro-toggle"
                className={cn(
                  'mt-4 w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200',
                  location.pathname.startsWith('/financeiro')
                    ? 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                )}
              >
                <Wallet className="w-5 h-5" />
                <span className="font-medium flex-1 text-left">Financeiro</span>
                <span className="text-[10px] uppercase tracking-wider opacity-70 bg-cyan-500/20 text-cyan-700 dark:text-cyan-200 px-1.5 py-0.5 rounded">Add-on</span>
                {finOpen ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              <div
                className={cn(
                  'overflow-hidden transition-all duration-300 ease-in-out pl-3 border-l border-cyan-500/30 ml-4',
                  finOpen ? 'max-h-[600px] opacity-100 mt-1' : 'max-h-0 opacity-0'
                )}
              >
                <div className="space-y-1">
                  {finMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={onItemClick}
                        data-testid={`nav-fin-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 group',
                          isActive(item.path)
                            ? 'bg-cyan-600 text-white'
                            : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                        )}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="font-medium">{item.label}</span>
                        {isActive(item.path) && (
                          <ChevronRight className="w-4 h-4 ml-auto" />
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {waMenuItems.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setWaOpen((v) => !v)}
                data-testid="nav-whatsapp-toggle"
                className={cn(
                  'mt-4 w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200',
                  location.pathname.startsWith('/whatsapp')
                    ? 'bg-green-500/10 text-green-700 dark:text-green-300'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                )}
              >
                <MessageCircle className="w-5 h-5" />
                <span className="font-medium flex-1 text-left">Atendimento</span>
                <span className="text-[10px] uppercase tracking-wider opacity-70 bg-green-500/20 text-green-700 dark:text-green-200 px-1.5 py-0.5 rounded">Add-on</span>
                {waOpen ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              <div
                className={cn(
                  'overflow-hidden transition-all duration-300 ease-in-out pl-3 border-l border-green-500/30 ml-4',
                  waOpen ? 'max-h-[400px] opacity-100 mt-1' : 'max-h-0 opacity-0'
                )}
              >
                <div className="space-y-1">
                  {waMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={onItemClick}
                        data-testid={`nav-wa-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 group',
                          isActive(item.path)
                            ? 'bg-green-600 text-white'
                            : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                        )}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="font-medium">{item.label}</span>
                        {isActive(item.path) && (
                          <ChevronRight className="w-4 h-4 ml-auto" />
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {(adminMenuItems.length > 0 || masterMenuItems.length > 0) && (
            <>
              <button
                type="button"
                onClick={() => setAdminOpen((v) => !v)}
                data-testid="nav-admin-toggle"
                className={cn(
                  'mt-4 w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200',
                  'text-muted-foreground hover:bg-secondary hover:text-foreground'
                )}
              >
                <Shield className="w-5 h-5" />
                <span className="font-medium flex-1 text-left">Administração</span>
                <span className="text-xs opacity-60">{adminMenuItems.length + masterMenuItems.length}</span>
                {adminOpen ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              <div
                className={cn(
                  'overflow-hidden transition-all duration-300 ease-in-out pl-3 border-l border-border/60 ml-4',
                  adminOpen ? 'max-h-[800px] opacity-100 mt-1' : 'max-h-0 opacity-0'
                )}
              >
                <div className="space-y-1">
                  {adminMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={onItemClick}
                        data-testid={`nav-admin-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 group',
                          isActive(item.path)
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                        )}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="font-medium">{item.label}</span>
                        {isActive(item.path) && (
                          <ChevronRight className="w-4 h-4 ml-auto" />
                        )}
                      </Link>
                    );
                  })}

                  {masterMenuItems.length > 0 && adminMenuItems.length > 0 && (
                    <div className="my-2 border-t border-border/60" />
                  )}

                  {masterMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={onItemClick}
                        data-testid={`nav-master-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 group',
                          isActive(item.path)
                            ? 'bg-destructive text-destructive-foreground'
                            : 'text-destructive/80 hover:bg-destructive/10 hover:text-destructive'
                        )}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="font-medium">{item.label}</span>
                        {isActive(item.path) && (
                          <ChevronRight className="w-4 h-4 ml-auto" />
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Legacy section removed - Master itens are now inside Administração accordion */}
        </nav>
      </ScrollArea>

      {/* User info */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center gap-3 px-2">
          <Avatar className="w-9 h-9">
            <AvatarFallback className="bg-primary/10 text-primary">
              {user?.nome?.charAt(0)?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.nome}</p>
            <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const AtividadesBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const fetchData = useCallback(() => {
    fetch(`${API_URL}/api/atividades/unread-list`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('agrolink_token') || ''}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) { setItems(d.items || []); setTotal(d.total || 0); } })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-full"
          data-testid="atividades-bell"
          title={`${total} atividade(s) não lida(s)`}
        >
          <ClipboardList className="h-5 w-5" />
          {total > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500 text-white"
              data-testid="atividades-count"
            >
              {total > 9 ? '9+' : total}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 max-h-[28rem] overflow-auto">
        <DropdownMenuLabel className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-red-500" />
          Atividades não lidas ({total})
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {total === 0 ? (
          <div className="p-4 text-center text-muted-foreground text-sm">
            Nenhuma atividade nova
          </div>
        ) : (
          items.slice(0, 15).map((it) => (
            <DropdownMenuItem
              key={it.id}
              className="flex flex-col items-start gap-0.5 p-3 cursor-pointer"
              onClick={() => navigate(`/atividades?id=${it.id}`)}
              data-testid={`atividade-notif-${it.id}`}
            >
              <div className="flex w-full items-start justify-between gap-2">
                <p className="font-medium text-sm flex-1 truncate">{it.titulo}</p>
                <span className={
                  it.motivo === 'Nova mensagem' ? 'text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500 text-white font-medium whitespace-nowrap' :
                  it.motivo === 'Atividade finalizada' ? 'text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500 text-white font-medium whitespace-nowrap' :
                  'text-[10px] px-1.5 py-0.5 rounded-full bg-red-500 text-white font-medium whitespace-nowrap'
                }>
                  {it.motivo}
                </span>
              </div>
              <p className="text-xs text-muted-foreground truncate w-full">
                {it.outro_nome ? `${it.outro_nome} · ` : ''}{it.projeto_nome || '—'}
                {it.projeto_numero ? ` · ${it.projeto_numero}` : ''}
              </p>
            </DropdownMenuItem>
          ))
        )}
        {total > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="justify-center text-xs text-primary cursor-pointer"
              onClick={() => navigate('/atividades')}
              data-testid="atividades-bell-see-all"
            >
              Ver todas as atividades
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const PagamentosBell = () => {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  const fetchItems = useCallback(async () => {
    try {
      const res = await pagamentosAPI.notifications();
      setItems(res.data?.items || []);
    } catch {
      // silencioso
    }
  }, []);

  useEffect(() => {
    fetchItems();
    const interval = setInterval(fetchItems, 5 * 60 * 1000); // 5 min
    return () => clearInterval(interval);
  }, [fetchItems]);

  const total = items.length;
  const totalValor = items.reduce((s, it) => s + (it.valor || 0), 0);
  const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-full"
          data-testid="pagamentos-bell"
          title={`${total} parcela(s) vencendo hoje`}
        >
          <DollarSign className="h-5 w-5" />
          {total > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-emerald-500 text-white"
              data-testid="pagamentos-count"
            >
              {total > 9 ? '9+' : total}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-auto">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-emerald-500" />
            Pagamentos Hoje ({total})
          </span>
          {total > 0 && (
            <span className="text-xs font-mono text-emerald-600">{fmtBRL(totalValor)}</span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {total === 0 ? (
          <div className="p-4 text-center text-muted-foreground text-sm">
            Nenhum pagamento agendado para hoje
          </div>
        ) : (
          items.slice(0, 15).map((it) => {
            const target = it.status_projeto === 'arquivado'
              ? `/projetos/arquivados?project=${it.projeto_id}`
              : `/projetos?project=${it.projeto_id}`;
            return (
            <DropdownMenuItem
              key={it.parcela_id}
              className="flex flex-col items-start gap-0.5 p-3 cursor-pointer"
              onClick={() => navigate(target)}
              data-testid={`pagamento-notif-${it.parcela_id}`}
            >
              <div className="flex w-full justify-between items-center gap-2">
                <p className="font-medium truncate flex-1">{it.cliente_nome}</p>
                <p className="text-xs font-mono text-emerald-600 whitespace-nowrap">{fmtBRL(it.valor)}</p>
              </div>
              <p className="text-[10px] text-muted-foreground truncate w-full">
                {it.tipo_projeto || 'Projeto'}
                {it.numero_interno ? ` · ${it.numero_interno}` : ''}
                {it.status_projeto === 'arquivado' ? ' · ARQUIVADO' : ''}
              </p>
            </DropdownMenuItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const LaudosBell = () => {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  const fetchLaudos = useCallback(async () => {
    try {
      const res = await laudosAPI.notifications();
      setItems(res.data?.items || []);
    } catch (e) {
      // silencioso (mantém UX limpa se endpoint não estiver disponível)
    }
  }, []);

  useEffect(() => {
    fetchLaudos();
    const interval = setInterval(fetchLaudos, 5 * 60 * 1000); // 5 min
    return () => clearInterval(interval);
  }, [fetchLaudos]);

  const total = items.length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-full"
          data-testid="laudos-bell"
          title={`${total} laudo(s) com data de hoje`}
        >
          <FileSignature className="h-5 w-5" />
          {total > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-amber-500 text-white"
              data-testid="laudos-count"
            >
              {total > 9 ? '9+' : total}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-auto">
        <DropdownMenuLabel className="flex items-center gap-2">
          <FileSignature className="w-4 h-4 text-amber-500" />
          Laudos de Hoje ({total})
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {total === 0 ? (
          <div className="p-4 text-center text-muted-foreground text-sm">
            Nenhum laudo agendado para hoje
          </div>
        ) : (
          items.slice(0, 15).map((it) => {
            const target = it.status_projeto === 'arquivado'
              ? `/projetos/arquivados?project=${it.projeto_id}`
              : `/projetos?project=${it.projeto_id}`;
            return (
            <DropdownMenuItem
              key={it.laudo_id}
              className="flex flex-col items-start gap-0.5 p-3 cursor-pointer"
              onClick={() => navigate(target)}
              data-testid={`laudo-notif-${it.laudo_id}`}
            >
              <p className="font-medium truncate w-full">{it.cliente_nome}</p>
              <p className="text-xs text-muted-foreground truncate w-full">
                {it.descricao}
              </p>
              <p className="text-[10px] text-primary truncate w-full">
                {it.tipo_projeto || 'Projeto'}
                {it.numero_interno ? ` · ${it.numero_interno}` : ''}
                {it.status_projeto === 'arquivado' ? ' · ARQUIVADO' : ''}
              </p>
            </DropdownMenuItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const NotificationBell = () => {
  const [oculto, setOculto] = useState(true);   // só aparece depois de confirmar que Propostas não está oculta
  useEffect(() => {
    getEmpresaPublicInfo().then(d => setOculto(!!d.ocultar_propostas));
    const onInfo = (e) => setOculto(!!e.detail?.ocultar_propostas);
    window.addEventListener('empresa-info', onInfo);
    return () => window.removeEventListener('empresa-info', onInfo);
  }, []);
  if (oculto) return null;
  return <PropostasBell />;
};

const PropostasBell = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await alertsAPI.getPropostas();
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Erro ao buscar alertas:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    // Refresh alerts every 5 minutes
    const interval = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const openWhatsApp = (telefone) => {
    if (!telefone) return;
    const phone = telefone.replace(/\D/g, '');
    const formattedPhone = phone.startsWith('55') ? phone : `55${phone}`;
    window.open(`https://wa.me/${formattedPhone}`, '_blank');
  };

  const handleAlertClick = (alert) => {
    navigate('/propostas');
  };

  const handleClearAll = async () => {
    try {
      await alertsAPI.clearAllPropostaAlerts();
      fetchAlerts();
    } catch (error) {
      console.error('Erro ao limpar alertas:', error);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-full"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {alerts.length > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-destructive"
              data-testid="notification-count"
            >
              {alerts.length > 9 ? '9+' : alerts.length}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-auto">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Propostas em Aberto ({alerts.length})
          </span>
          {alerts.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                handleClearAll();
              }}
            >
              Limpar
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {loading ? (
          <div className="p-4 text-center text-muted-foreground text-sm">
            Carregando...
          </div>
        ) : alerts.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground text-sm">
            Nenhuma proposta pendente
          </div>
        ) : (
          alerts.slice(0, 10).map((alert) => (
            <DropdownMenuItem
              key={alert.id}
              className="flex items-start gap-3 p-3 cursor-pointer"
              onClick={() => handleAlertClick(alert)}
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{alert.cliente_nome}</p>
                <p className="text-xs text-muted-foreground">
                  {alert.dias_aberta} dias aberta • Alerta {alert.alerta_numero}/3
                </p>
                <p className="text-xs text-primary truncate">
                  {alert.tipo_projeto} • {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(alert.valor_credito)}
                </p>
              </div>
              {alert.telefone && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    openWhatsApp(alert.telefone);
                  }}
                >
                  <MessageCircle className="w-4 h-4 text-green-600" />
                </Button>
              )}
            </DropdownMenuItem>
          ))
        )}
        {alerts.length > 10 && (
          <>
            <DropdownMenuSeparator />
            <div className="p-2 text-center text-xs text-muted-foreground">
              E mais {alerts.length - 10} propostas...
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });
  const [userPermissions, setUserPermissions] = useState([]);
  const { whatsappAtivo } = useModulosAtivos();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const toggleSidebar = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    localStorage.setItem('sidebar_collapsed', String(next));
  };

  // Verificar manutenção periodicamente (a cada 10 segundos)
  // Se o usuário NÃO é master e manutenção ativa, redireciona
  useEffect(() => {
    if (!user || user.role === 'master') return;

    const checkManutencao = () => {
      fetch(`${API_URL}/api/manutencao/status`)
        .then(r => r.json())
        .then(data => {
          if (data.manutencao) {
            window.location.href = '/manutencao';
          }
        })
        .catch(() => {});
    };

    checkManutencao();
    const interval = setInterval(checkManutencao, 10000);
    return () => clearInterval(interval);
  }, [user]);

  // Buscar permissões do usuário ao carregar
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await cargosAPI.getMinhasPermissoes();
        setUserPermissions(response.data.paginas || []);
      } catch (error) {
        console.error('Erro ao buscar permissões:', error);
        // Fallback: se erro, dar acesso ao dashboard pelo menos
        setUserPermissions(['/']);
      }
    };
    
    if (user) {
      fetchPermissions();
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside
        className={cn(
          'hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:block lg:bg-card lg:border-r lg:border-border transition-all duration-300',
          sidebarCollapsed ? 'lg:w-0 lg:overflow-hidden' : 'lg:w-64'
        )}
      >
        <SidebarContent userPermissions={userPermissions} />
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-64 p-0">
          <SidebarContent onItemClick={() => setMobileOpen(false)} userPermissions={userPermissions} />
        </SheetContent>
      </Sheet>

      {/* Main content */}
      <div className={cn('transition-all duration-300', sidebarCollapsed ? 'lg:pl-0' : 'lg:pl-64')}>
        {/* Header */}
        <header className="sticky top-0 z-40 glass border-b border-border">
          <div className="flex h-16 items-center gap-4 px-4 sm:px-6 lg:px-8">
            {/* Mobile menu button */}
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden"
                  onClick={() => setMobileOpen(true)}
                  data-testid="mobile-menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
            </Sheet>

            {/* Desktop sidebar toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="hidden lg:flex"
              onClick={toggleSidebar}
              data-testid="toggle-sidebar"
            >
              {sidebarCollapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
            </Button>

            <div className="flex-1" />

            {/* Atendimento WhatsApp: mensagens novas, transferências e menções (com som) */}
            {whatsappAtivo && userPermissions.includes('/whatsapp') && <AlertasWA />}

            {/* Atividades Bell */}
            <AtividadesBell />

            {/* Laudos Bell (Fase 3) */}
            <LaudosBell />

            {/* Pagamentos Bell (Fase 4) */}
            <PagamentosBell />

            {/* Notification Bell */}
            <NotificationBell />

            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="header-theme-toggle"
              className="rounded-full"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-primary/10 text-primary text-sm">
                      {user?.nome?.charAt(0)?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline font-medium">{user?.nome}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{user?.nome}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Banner de vencimento (apenas master) */}
        <VencimentoBanner />

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>

      {/* Chat flutuante (só monta se chat_enabled) */}
      <ChatWidget />
    </div>
  );
};

export default Layout;
